import { useEffect,useState,useRef } from "react"
import { Button } from "@/components/ui/button"
import { ImagePlus,Trash,Loader2,Upload,Plus } from "lucide-react"
import { isCancel } from '@/lib/api-client'
import { uploadImage } from '@/lib/image-upload'
import { toast } from 'sonner'
import { cn } from "@/lib/utils"

interface ImageUploadProps {
  disabled?: boolean
  onChange: (value: string) => void
  onRemove: (value: string) => void
  value: string[]
  onUploadStart?: () => void
  onUploadEnd?: () => void
  variant?: "default" | "avatar" | "large" | "thumbnail"
  manualUpload?: boolean
  onFileSelect?: (file: File, previewUrl: string) => void
  /** Key prefix under the org on the CDN, e.g. `products`, `logos`. */
  folder?: string
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  disabled,
  onChange,
  onRemove,
  value,
  onUploadStart,
  onUploadEnd,
  variant = "avatar",
  manualUpload = false,
  onFileSelect,
  folder,
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [cancelToken, setCancelToken] = useState<AbortController | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)


  useEffect(() => {
    setIsMounted(true)
  }, [])

  const onCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (cancelToken) {
      cancelToken.abort()
      setCancelToken(null)
      setIsLoading(false)
      setProgress(0)
      onUploadEnd?.()
      toast.info("Upload Cancelled", { description: "The image upload was cancelled." })
    }
  }

  const onUpload = async (file: File) => {
    if (!file) return

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large", { description: "Image must be less than 2MB" })
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      return
    }

    if (manualUpload && onFileSelect) {
      const previewUrl = URL.createObjectURL(file)
      onChange(previewUrl)
      onFileSelect(file, previewUrl)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      return
    }

    try {
      setIsLoading(true)
      onUploadStart?.()
      setProgress(0)

      const controller = new AbortController()
      setCancelToken(controller)

      const url = await uploadImage(file, { folder, signal: controller.signal, onProgress: setProgress })

      onChange(url)

      toast.success("Success", { description: "Image uploaded successfully" })
    } catch (error: unknown) {
      if (isCancel(error) || (error as { name?: string }).name === "AbortError") {
        // upload was intentionally cancelled — no error toast
      } else {
        toast.error("Error", { description: "Something went wrong during upload." })
      }
    } finally {
      setIsLoading(false)
      onUploadEnd?.()
      setCancelToken(null)
      setProgress(0)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  if (!isMounted) {
    return null
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      onUpload(file)
    }
  }

  const currentInfo = value?.[0]

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4",
        variant !== "thumbnail" && "w-full"
      )}
    >
      <div
        className={cn(
          "relative group flex flex-col items-center justify-center cursor-pointer overflow-hidden transition-all duration-300",
          variant === "avatar"
            ? "w-32 h-32 rounded-md border-2 border-dashed border-input hover:border-sidebar-ring hover:bg-accent/10"
            : variant === "large"
              ? "w-64 h-64 rounded-lg border-2 border-dashed border-input hover:border-sidebar-ring hover:bg-accent/10"
              : variant === "thumbnail"
                ? "w-full h-full aspect-square rounded-md border-2 border-dashed border-input hover:border-sidebar-ring hover:bg-accent/10"
                : "w-32 h-32 rounded-lg border-2 border-dashed border-input hover:border-sidebar-ring hover:bg-accent/10",
          (isLoading || disabled) && "opacity-80 cursor-not-allowed pointer-events-none",
          isDragging && "border-sidebar-ring bg-accent/10 scale-105 shadow-md"
        )}
        onClick={() => !isLoading && fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          type="file"
          accept="image/*"
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) onUpload(file)
          }}
          disabled={disabled || isLoading}
        />

        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-20">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-sidebar-ring" />
              <span className="absolute text-[10px] font-medium text-sidebar-ring mt-8">
                {progress}%
              </span>
            </div>
            <Button
              variant="destructive"
              size="sm"
              className="absolute bottom-2 h-6 text-[10px] px-2"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        ) : null}

        {currentInfo ? (
          <>
            <img
              src={currentInfo}
              alt="Upload"
              className="object-cover w-full h-full"
            />
            <div
              className={cn(
                "absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                isLoading && "hidden"
              )}
            >
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove(currentInfo)
                  }}
                  variant="destructive"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-muted-foreground p-4 text-center">
            {variant === "avatar" ? (
              <ImagePlus className="w-8 h-8 mb-2 opacity-50" />
            ) : variant === "thumbnail" ? (
              <Plus className="w-6 h-6 opacity-50" />
            ) : (
              <Upload className="w-8 h-8 mb-2 opacity-50" />
            )}
            {variant !== "avatar" && variant !== "thumbnail" && (
              <span className="text-xs font-medium">Click to upload</span>
            )}
          </div>
        )}
      </div>

      {!currentInfo && variant !== "thumbnail" && (
        <div className="text-center space-y-1">
          <h4 className="text-sm font-medium">
            {isLoading ? "Uploading..." : "Upload Image"}
          </h4>
          <p className="text-xs text-muted-foreground">PNG, JPG up to 2MB</p>
        </div>
      )}
    </div>
  )
}
